import { utcToZonedTime } from "date-fns-tz";
import { formatWithTimeZone, DAY_MS, MONTH_MS, YEAR_MS } from "../../core";
export function dateFormatOptionsWithTimeZone(dateFormatOptions, timeZone) {
    /*
   * if timeZone is provided, and is not local|browser,
   * then set timeZone option (recognize UTC regardless of uppercase/lowercase)
   * otherwise, default to browser timeZone setting
   */ if (timeZone) {
        const lowerTimeZone = timeZone.toLowerCase();
        if (lowerTimeZone !== 'local' && lowerTimeZone !== 'browser') {
            return {
                ...dateFormatOptions,
                timeZone: lowerTimeZone === 'utc' ? 'UTC' : timeZone
            };
        }
    }
    return dateFormatOptions;
}
/*
 * Determines time granularity for axis labels, defaults to hh:mm
 */ export function getFormattedDate(value, rangeMs, timeZone) {
    const dateFormatOptions = dateFormatOptionsWithTimeZone({
        hour: 'numeric',
        minute: 'numeric',
        hourCycle: 'h23'
    }, timeZone);
    const thirtyMinMs = 1800000;
    const dayMs = 86400000;
    if (rangeMs <= thirtyMinMs) {
        dateFormatOptions.second = 'numeric';
    } else if (rangeMs >= dayMs) {
        dateFormatOptions.month = 'numeric';
        dateFormatOptions.day = 'numeric';
    }
    const DATE_FORMAT = new Intl.DateTimeFormat(undefined, dateFormatOptions);
    // remove comma when month / day present
    return DATE_FORMAT.format(value).replace(/, /g, ' ');
}
/*
 * Determines time granularity for cateogry axis labels, used in deprecated LineChartBase
 */ export function legacyGetFormattedDate(label, rangeMs, timeZone) {
    // Category axis passes timestamp as string so convert to number
    const date = new Date(Number(label));
    let format;
    if (rangeMs > YEAR_MS * 5) {
        // more than 5 years
        format = 'yyyy';
    } else if (rangeMs > YEAR_MS * 2) {
        // more than 2 years
        format = 'MMM yyyy';
    } else if (rangeMs > DAY_MS) {
        // more than one day
        format = 'M/dd HH:mm';
    } else {
        // less than one day
        format = 'HH:mm';
    }
    return formatWithTimeZone(date, format, timeZone);
}
// https://echarts.apache.org/en/option.html#xAxis.axisLabel.formatter
export function getFormattedAxisLabel(rangeMs) {
    // more than 5 years
    if (rangeMs > YEAR_MS * 5) {
        return '{yyyy}';
    }
    // more than 2 years
    if (rangeMs > YEAR_MS * 2) {
        return '{MMM} {yyyy}';
    }
    // between 5 days to 6 months
    if (rangeMs > DAY_MS * 5 && rangeMs < MONTH_MS * 6) {
        return '{MM}/{dd}'; // 12/01
    }
    // between 2 and 5 days
    if (rangeMs > DAY_MS * 2 && rangeMs <= DAY_MS * 5) {
        return '{MM}/{dd} {HH}:{mm}'; // 12/01 12:30
    }
    return {
        year: '{yearStyle|{yyyy}}\n{monthStyle|{MMM}}',
        month: '{MMM}',
        day: '{MM}/{dd}'
    };
}
/**
 * Custom time axis label formatter to control format based on active time range and user's preferred time zone
 * Workaround is using similar approach as: https://github.com/apache/echarts/issues/20027#issuecomment-2165555578
 */ export function getFormattedTimeAxisLabel(value, rangeMs, timeZone) {
    const date = new Date(value);
    // Determine the appropriate format based on the time range
    let format;
    if (rangeMs > YEAR_MS * 5) {
        // more than 5 years
        format = 'yyyy';
    } else if (rangeMs > YEAR_MS * 2) {
        // more than 2 years
        format = 'MMM yyyy';
    } else if (rangeMs > DAY_MS * 5 && rangeMs < MONTH_MS * 6) {
        // between 5 days to 6 months
        format = 'M/dd';
    } else if (rangeMs > DAY_MS * 2 && rangeMs <= DAY_MS * 5) {
        // between 2 and 5 days
        format = 'M/dd HH:mm';
    } else if (rangeMs <= DAY_MS * 2 && rangeMs > 0) {
        // up to 2 days
        format = isStartOfDayInTimeZone(date, timeZone) ? 'M/dd' : 'HH:mm'; // show date when a new day is reached
    } else {
        // Fallback, unlikely to be used
        format = 'HH:mm:ss';
    }
    return formatWithTimeZone(date, format, timeZone);
}
export function isStartOfDayInTimeZone(date, timeZone) {
    const zonedDate = timeZone !== undefined && timeZone !== 'local' ? utcToZonedTime(date, timeZone) : date;
    const isStartOfDay = zonedDate.getHours() === 0 && zonedDate.getMinutes() === 0 && zonedDate.getSeconds() === 0;
    return isStartOfDay;
}

//# sourceMappingURL=format.js.map